// utils/firmassistTime.js
// ============================================================================
// FirmAssist's canonical business/legal-event timezone.
//
//   FirmAssist's canonical business/legal-event timezone is Africa/Accra.
//
// A user's physical/browser/device timezone must NEVER change the meaning
// or status of a hearing, filing deadline, task, matter deadline,
// reminder-eligibility window, overdue/upcoming status, or any other
// legal-event calendar date — a hearing entered as "8 September 2026,
// 08:00" means 08:00 Africa/Accra, whether the viewer is in Accra, Calgary,
// or Sydney.
//
// Ghana does not observe daylight saving time, so Africa/Accra is UTC+0
// year-round. That makes "treat the stored dueDate/dueTime as UTC" and
// "treat it as Africa/Accra" numerically identical *today* — but the
// domain rule is Africa/Accra, not "UTC" (an infrastructure timezone that
// happens to coincide with it). Every function below is written and
// documented in Accra terms, with the UTC-equivalence noted only as the
// current implementation detail that makes it work with plain `Date`.
//
// This module is the ONE place `dueDate`/`dueTime` legal-event strings are
// turned into `Date` instants or compared against "now" — do not scatter
// new `new Date(...)` arithmetic through components; import from here.
//
// Backend parity: functions/notificationOrchestrator.js's own
// `combineDateTime` uses this exact same construction
// (`${dueDate}T${dueTime}:00Z`) independently, server-side — this module
// exists so the frontend shares that same semantic contract instead of
// drifting via browser-local `Date` parsing.
//
// OUT OF SCOPE: createdAt/updatedAt and Firestore serverTimestamp() values
// are absolute infrastructure timestamps, not legal-event dates — they are
// untouched by this module and should keep using plain Date/Timestamp
// handling.
// ============================================================================

export const ACCRA_TIMEZONE = 'Africa/Accra';

const NON_ACTIONABLE_STATUSES = new Set(['completed', 'done', 'cancelled']);

/**
 * Combine a legal-event `dueDate` ("YYYY-MM-DD") and optional `dueTime`
 * ("HH:MM") into the exact Africa/Accra instant they represent, as a
 * `Date`. Returns null (never throws) for a missing/malformed dueDate.
 *
 * When dueTime is omitted, this returns Accra midnight of that date — the
 * START of the legal-event day, not a meaningful "due moment". Most
 * call sites that need date-only semantics should use
 * `accraEndOfDayInstant` (the END of the day) instead, so a genuinely
 * date-only deadline isn't treated as overdue from the stroke of
 * midnight — see `isOverdue` below.
 */
export function combineAccraDateTime(dueDate, dueTime) {
  if (!dueDate) return null;
  const time = dueTime || '00:00';
  const d = new Date(`${dueDate}T${time}:00Z`); // Africa/Accra === UTC+0, year-round — see module header
  return isNaN(d.getTime()) ? null : d;
}

/**
 * The last instant of a legal-event day in Africa/Accra (23:59:59.999) —
 * the correct reference point for a genuinely date-only record (no
 * dueTime), so it doesn't read as overdue before the day is actually over.
 */
export function accraEndOfDayInstant(dueDate) {
  if (!dueDate) return null;
  const d = new Date(`${dueDate}T23:59:59.999Z`);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * The Africa/Accra calendar date ("YYYY-MM-DD") that a given instant
 * falls on. Because Accra is UTC+0, this is simply the instant's own UTC
 * calendar date — computed without ever touching the browser's local
 * timezone, which is exactly what makes this safe to call from any
 * device, anywhere.
 */
export function accraCalendarDateString(instant = new Date()) {
  return instant.toISOString().slice(0, 10);
}

/**
 * Normalize a "YYYY-MM-DD" string, a `Date`, or a Firestore-Timestamp-like
 * value (anything with a `.toDate()` method) into an Accra calendar-date
 * string. Returns null (never throws) for anything else/invalid.
 */
function toAccraDateString(value) {
  if (!value) return null;
  if (typeof value === 'string') return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
  if (typeof value.toDate === 'function') return accraCalendarDateString(value.toDate());
  if (value instanceof Date) return accraCalendarDateString(value);
  return null;
}

/**
 * Whole calendar-day difference (Accra terms) between `dueDate` and
 * `now`: positive = due in the future, 0 = due today, negative = overdue
 * by that many days. Pure calendar-date arithmetic — never affected by
 * dueTime or by the viewer's browser timezone. Accepts a "YYYY-MM-DD"
 * string, a `Date`, or a Firestore Timestamp.
 */
export function daysUntilAccra(dueDate, now = new Date()) {
  const dateStr = toAccraDateString(dueDate);
  if (!dateStr) return null;
  const due = new Date(`${dateStr}T00:00:00Z`);
  if (isNaN(due.getTime())) return null;
  const today = new Date(`${accraCalendarDateString(now)}T00:00:00Z`);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

export function isTodayAccra(dueDate, now = new Date()) {
  return daysUntilAccra(dueDate, now) === 0;
}

export function isTomorrowAccra(dueDate, now = new Date()) {
  return daysUntilAccra(dueDate, now) === 1;
}

/** True when `dueDate` falls within the next `days` calendar days (inclusive of today), Accra terms. */
export function isUpcomingAccra(dueDate, now = new Date(), days = 7) {
  const diff = daysUntilAccra(dueDate, now);
  return diff !== null && diff >= 0 && diff <= days;
}

/**
 * Correct overdue semantics for a legal-event record:
 *   - completed/done/cancelled records are never overdue.
 *   - a record with a real dueTime is overdue only once that exact
 *     Africa/Accra instant has passed — NOT at midnight.
 *   - a genuinely date-only record (no dueTime) is overdue only once the
 *     entire Accra calendar day has elapsed (end-of-day), not from the
 *     start of its due date.
 */
export function isOverdue({ dueDate, dueTime, status } = {}, now = new Date()) {
  if (!dueDate) return false;
  if (status && NON_ACTIONABLE_STATUSES.has(String(status).toLowerCase())) return false;
  const reference = dueTime ? combineAccraDateTime(dueDate, dueTime) : accraEndOfDayInstant(dueDate);
  if (!reference) return false;
  return now.getTime() > reference.getTime();
}

/** Hours between `now` and the combined Accra due instant — for court-reminder-style tiered windows (48h/24h/2h). Positive = still in the future. */
export function hoursUntilAccra(dueDate, dueTime, now = new Date()) {
  const target = combineAccraDateTime(dueDate, dueTime);
  if (!target) return null;
  return (target.getTime() - now.getTime()) / (1000 * 60 * 60);
}

/**
 * Format a legal-event date/time for display, anchored to Africa/Accra so
 * the rendered text never shifts with the viewer's device timezone.
 * `timeZone: 'UTC'` is used because it's numerically identical to
 * Africa/Accra (see module header) and has universal Intl support;
 * documented here rather than at every call site.
 */
export function formatAccraDate(dueDate, options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, locale = 'en-GB') {
  const dateStr = toAccraDateString(dueDate);
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
}

export function formatAccraTime(dueTime) {
  if (!dueTime) return '';
  const d = new Date(`1970-01-01T${dueTime}:00Z`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}

export function formatAccraDateTime(dueDate, dueTime, options) {
  const date = formatAccraDate(dueDate, options);
  const time = dueTime ? formatAccraTime(dueTime) : '';
  return time ? `${date} at ${time}` : date;
}

/**
 * Safe, crash-proof comparator for sorting by dueDate directly (raw
 * values, not task objects) — a drop-in replacement for
 * `(a.dueDate || '').localeCompare(b.dueDate || '')`, which throws if a
 * dueDate is ever a Firestore Timestamp (no `.localeCompare` method on
 * one) and sorts a genuinely empty/unknown dueDate BEFORE real dated
 * work (an empty string sorts lexicographically first). Here, unknown/
 * invalid dates sort AFTER known ones instead, and this never throws
 * regardless of the input's shape (string, Timestamp, Date, null,
 * undefined, or anything else — see toAccraDateString).
 */
export function compareDueDatesForSort(a, b) {
  const da = toAccraDateString(a);
  const db = toAccraDateString(b);
  if (da && db) return da < db ? -1 : da > db ? 1 : 0;
  if (da) return -1;
  if (db) return 1;
  return 0;
}
